import * as stylex from "@stylexjs/stylex";
import { Bell, BellOff, Lock, Plus, Repeat } from "lucide-react";
import { Fragment, type KeyboardEvent } from "react";
import { copy } from "@/copy";
import type { Breakpoint } from "@/domain/breakpoint";
import { type DefaultWord, type EventWord, isPlaceholder, type SentenceWord, sentenceTokens, whoText } from "@/domain/eventSentence";
import type { Tag, TagColor } from "@/domain/schemas";
import { type Clock, formatClock, formatLongWeekday, formatMonthDay, formatWeekday } from "@/domain/time";
import { parseDateKey } from "@/domain/zone";
import "@/theme/seasons.stylex";
import { season } from "@/theme/seasons.stylex";
import "@/theme/tokens.stylex";
import { colors, motion, radii } from "@/theme/tokens.stylex";
import { Icon } from "@/ui/Icon";
import { press } from "@/ui/press";
import { tagFills } from "@/ui/tagFills";
import type { PageDraft } from "../eventPage";
export type SameTime = {
  tagIds: readonly string[];
  title: string;
  start: number;
  end: number;
  more: number;
};
type EventSentenceProps = {
  breakpoint: Breakpoint;
  mode: "edit" | "view";
  draft: PageDraft;
  tags: readonly Tag[];
  touched: readonly DefaultWord[];
  word: EventWord | null;
  clock: Clock;
  locked?: boolean;
  sameTime?: SameTime;
  onSelect: (word: EventWord) => void;
  onAddEnd: () => void;
  onNotify: (on: boolean) => void;
};
type Piece = {
  text: string;
  placeholder: boolean;
  wrap: boolean;
  color?: TagColor;
};
type WordProps = {
  word: SentenceWord;
  piece: Piece;
  mode: "edit" | "view";
  locked: boolean;
  selected: boolean;
  thin: boolean;
  onSelect: (word: EventWord) => void;
};
const words = copy.events.page;
const VIEW_NAMED = 3;
const VIEW_SHOWN = 2;
const styles = {
  card: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kmuXW: "x2lah0s",
    kaIpWk: "xwe7p7i",
    kWkggS: "x1ubcl0s",
    k1ekBW: "x15406qy",
    kIyJzY: "x11l8gmq",
    kAMwcw: "x1eyvnp3",
    $$css: true
  },
  sentence: {
    kogj98: "x1ghz6dp",
    k63SB2: "x1v94iar",
    kLWn49: "xx6cpbe",
    kN2L0X: "x1fzhlzt",
    $$css: true
  },
  word: {
    k1xSpc: "xt0psk2",
    kUOVxO: "xen9kdk",
    k8WAf4: "x16vc99w",
    kg3NbH: "x1wvu9w3",
    kaIpWk: "x1wz39yh",
    kWkggS: "xjbqb8w",
    kcSHmL: "xxxd787",
    keMLs9: "xyh5bb9",
    kPmA4f: "x19bzpp3",
    kkrTdU: "x1ypdohk",
    kI3sdo: "x1a2a7pz xyn84ga",
    k1ekBW: "x15406qy",
    kIyJzY: "x16c7our",
    $$css: true
  },
  set: {
    k63SB2: "xfbjfs5",
    kMwMTN: "x1a11ngh",
    kMnn75: "xujl8zx",
    kmVMDM: "x6ur5ct",
    $$css: true
  },
  placeholder: {
    k63SB2: "xo1l8bm",
    kMwMTN: "x19r2jmw",
    kMnn75: "xujl8zx",
    kmVMDM: "xev0dqp",
    $$css: true
  },
  viewed: {
    kMnn75: "xkrqix3",
    $$css: true
  },
  thick: {
    kNySMw: "x1gspyis",
    $$css: true
  },
  thin: {
    kNySMw: "xewurvo",
    $$css: true
  },
  nowrap: {
    khDVqt: "xuxw1ft",
    $$css: true
  },
  selected: {
    kWkggS: "xh540kj",
    $$css: true
  },
  locked: {
    kkrTdU: "xt0e3qv",
    $$css: true
  },
  dot: {
    k1xSpc: "x1rg5ohu",
    kzqmXN: "xhluxu3",
    kZKoxP: "x1vywnv8",
    km5ZXQ: "x11b4ncn",
    kaIpWk: "xqhrb7l",
    kGVxlE: "xjmit6t",
    kXLuUW: "xdnv9to",
    $$css: true
  },
  link: {
    kAzted: "x1gg8mnh",
    ks0D6T: "x193iq5w",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "x167g77z",
    k8WAf4: "xt970qd",
    kg3NbH: "x1xijbu4",
    kMzoRj: "xc342km",
    kaIpWk: "x1wz39yh",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1a11ngh",
    kMv6JI: "xjb2p0i",
    kGuDYH: "xosj86m",
    k63SB2: "x1cf7cre",
    kMnn75: "xujl8zx",
    kNySMw: "xewurvo",
    kcSHmL: "x5ek575",
    khDVqt: "xuxw1ft",
    kVQacm: "xb3r6kr",
    kg5iWk: "xlyipyv",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x18dpk69",
    kIyJzY: "xlc9y7w",
    $$css: true
  },
  notify: {
    koQZXg: "x8x9d4c",
    kAzted: "x1gg8mnh",
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1v2ro7d",
    k8WAf4: "xt970qd",
    kg3NbH: "x1xijbu4",
    kMzoRj: "xc342km",
    kaIpWk: "x1fv5t7x",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1a11ngh",
    kMv6JI: "xjb2p0i",
    kGuDYH: "xosj86m",
    k63SB2: "x1cf7cre",
    khDVqt: "xuxw1ft",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    $$css: true
  },
  bell: {
    koQZXg: "x8x9d4c",
    kmuXW: "x2lah0s",
    kzqmXN: "x187nhsf",
    kZKoxP: "xn3w4p2",
    k1xSpc: "xrvj5dj",
    kgQiWS: "x1ku5rj1",
    kmVPX3: "x1717udv",
    kMzoRj: "xc342km",
    kaIpWk: "xqhrb7l",
    kWkggS: "xjbqb8w",
    kMwMTN: "x1a11ngh",
    kkrTdU: "x1ypdohk",
    k1ekBW: "x11xpdln",
    kIyJzY: "x1bpocu2",
    $$css: true
  },
  notes: {
    keoZOQ: "x1gslohp",
    k1K539: "xat24cr",
    kUOVxO: "xrxpjvj",
    ks0D6T: "x7durtj",
    k63SB2: "x15i68ds",
    kLWn49: "x37zpob",
    kb6lSQ: "x1yrajzq",
    khDVqt: "x1fj9vlw",
    kN2L0X: "x1fzhlzt",
    $$css: true
  }
};
const sizes = {
  large: {
    kOIVth: "xou54vl",
    kLKAdn: "x1miatn0",
    kg3NbH: "x1sdxjpb",
    kGO01o: "xwxc41k",
    $$css: true
  },
  normal: {
    kOIVth: "x1v2ro7d",
    kLKAdn: "x1p5oq8j",
    kg3NbH: "xj7an9n",
    kGO01o: "x1l90r2v",
    $$css: true
  },
  small: {
    kOIVth: "x1v2ro7d",
    kLKAdn: "xyamay9",
    kg3NbH: "x17qtykl",
    kGO01o: "xsag5q8",
    $$css: true
  }
};
const sentenceSizes = {
  large: {
    kGuDYH: "x33rcho",
    kb6lSQ: "xv4e3ii",
    $$css: true
  },
  normal: {
    kGuDYH: "x1tgwohi",
    kb6lSQ: "xipi2l9",
    $$css: true
  },
  small: {
    kGuDYH: "xneq65o",
    kb6lSQ: "xrg9stw",
    $$css: true
  }
};
const notesSizes = {
  large: {
    kGuDYH: "x1ewwscv",
    $$css: true
  },
  normal: {
    kGuDYH: "x1i1m3gp",
    $$css: true
  },
  small: {
    kGuDYH: "xosj86m",
    $$css: true
  }
};
export function EventSentence(props: EventSentenceProps) {
  const {
    breakpoint,
    mode,
    draft,
    word,
    locked = false,
    onSelect
  } = props;
  const view = mode === "view";
  const tokens = sentenceTokens({
    allDay: draft.allDay,
    hasEnd: draft.end !== undefined,
    hasPlace: draft.location.trim() !== "",
    view,
    weekly: draft.repeat === "weekly"
  });
  return <section aria-label={words.summary} {...stylex.props(styles.card, sizes[breakpoint])}>
      <p {...stylex.props(styles.sentence, sentenceSizes[breakpoint])}>
        {tokens.map(token => <Fragment key={token.word}>
            {token.join && words.joins[token.join]}
            {wordPieces(token.word, props).map((piece, index) => typeof piece === "string" ?
        // biome-ignore lint/suspicious/noArrayIndexKey: joins are fixed text in a fixed order
        <Fragment key={index}>{piece}</Fragment> : <Word
        // biome-ignore lint/suspicious/noArrayIndexKey: the pieces of one word keep their order
        key={index} word={token.word} piece={piece} mode={mode} locked={locked} selected={word === token.word} thin={breakpoint === "small"} onSelect={onSelect} />)}
          </Fragment>)}
        {words.period}
      </p>
      {view ? <ViewLines {...props} /> : <LinkRows {...props} />}
    </section>;
}
function Word({
  word,
  piece,
  mode,
  locked,
  selected,
  thin,
  onSelect
}: WordProps) {
  const field = words.fields[word];
  const look = stylex.props(styles.word, piece.placeholder ? styles.placeholder : styles.set, mode === "view" && styles.viewed, thin ? styles.thin : styles.thick, !piece.wrap && styles.nowrap, selected && styles.selected, locked && styles.locked);
  const content = <>
      {piece.color && <span aria-hidden {...stylex.props(styles.dot, tagFills[piece.color])} />}
      {piece.text}
    </>;
  if (locked) return <span {...look}>{content}</span>;
  const select = () => onSelect(word);
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    select();
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: words stay inline text so they wrap inside the sentence (EVT-02)
    <span role="button" tabIndex={0} aria-pressed={selected} aria-label={wordName(field, piece, mode)} onClick={select} onKeyDown={onKeyDown} {...look}>
      {content}
    </span>
  );
}
function LinkRows({
  breakpoint,
  draft,
  tags,
  word,
  clock,
  sameTime,
  onSelect,
  onAddEnd,
  onNotify
}: EventSentenceProps) {
  const notes = draft.notes.trim();
  const on = draft.notify;
  return <>
      <div className="x78zum5 x1a02dak x6s0dn4 x2tj7o2 xubnld7">
        <button type="button" aria-pressed={word === "notes"} onClick={() => onSelect("notes")} {...stylex.props(styles.link, press.scale, word === "notes" && styles.selected)}>
          {notes === "" && <Icon icon={Plus} size={18} />}
          {notes === "" ? words.addNotes : words.notesPreview(notes)}
        </button>
        {draft.end === undefined && !draft.allDay && <button type="button" onClick={onAddEnd} {...stylex.props(styles.link, press.scale)}>
            <Icon icon={Plus} size={18} />
            {words.addEnd}
          </button>}
      </div>
      <div className="x78zum5 x1a02dak x6s0dn4 x2tj7o2 xubnld7">
        {breakpoint === "normal" && sameTime && <span role="status" className="x1gg8mnh xeuugli x3nfvp2 x6s0dn4 x1v2ro7d x1xijbu4 xosj86m x15i68ds x1xfvgam">
            <span aria-hidden className="x2lah0s xsmyaan x1kpxq89 xqhrb7l x7ns719" />
            <span>
              <span className="xfbjfs5">{words.sameTime}</span>{" "}
              {sameTimeText(sameTime, tags, clock)}
            </span>
          </span>}
        {breakpoint === "small" ? <button type="button" aria-label={words.notificationName(on)} onClick={() => onNotify(!on)} {...stylex.props(styles.bell, press.scale)}>
            <Icon icon={on ? Bell : BellOff} size={24} />
          </button> : <button type="button" role="switch" aria-checked={on} aria-label={words.notificationName(on)} onClick={() => onNotify(!on)} {...stylex.props(styles.notify, press.scale)}>
            <span aria-hidden {...{
          0: {
            className: "x1n2onr6 x2lah0s x100vrsf xxk0z11 xqhrb7l xh540kj x1ywt2or x15406qy x16c7our"
          },
          1: {
            className: "x1n2onr6 x2lah0s x100vrsf xxk0z11 xqhrb7l x1ywt2or x15406qy x16c7our x1i4uan3"
          }
        }[!!on << 0]}>
              <span {...{
            0: {
              className: "x10l6tqk xndqk7f xcqyyxr x1kky2od xlup9mm xqhrb7l x1i4uan3 x1c071of x18dpk69 x16c7our x1em5f5i"
            },
            1: {
              className: "x10l6tqk xndqk7f xcqyyxr x1kky2od xlup9mm xqhrb7l x18dpk69 x16c7our x1em5f5i x1f7iotj x1psfjxj"
            }
          }[!!on << 0]} />
            </span>
            {words.notification}
          </button>}
      </div>
    </>;
}
function ViewLines({
  breakpoint,
  draft,
  locked
}: EventSentenceProps) {
  const notes = draft.notes.trim();
  return <>
      {notes !== "" && <p {...stylex.props(styles.notes, notesSizes[breakpoint])}>{notes}</p>}
      {!draft.notify && <div className="x1gg8mnh x78zum5 x6s0dn4 x1v2ro7d xosj86m x1cf7cre">
          <Icon icon={BellOff} size={18} />
          {words.notificationOff}
        </div>}
      {draft.repeat === "weekly" && <div className="x1gg8mnh x78zum5 x6s0dn4 x1v2ro7d xosj86m x1cf7cre">
          <Icon icon={Repeat} size={18} />
          {words.repeatsFrom(dayText(draft.firstDay ?? draft.day))}
        </div>}
      {locked && <div className="x1gg8mnh x78zum5 x6s0dn4 x1v2ro7d xosj86m x1cf7cre">
          <Icon icon={Lock} size={18} />
          {words.ended}
        </div>}
    </>;
}
function wordPieces(word: SentenceWord, props: EventSentenceProps): (Piece | string)[] {
  const {
    mode,
    draft,
    touched,
    clock
  } = props;
  const placeholder = mode === "edit" && isPlaceholder(word, draft, touched);
  const fixed = (text: string): Piece => ({
    text,
    placeholder,
    wrap: false
  });
  const free = (text: string): Piece => ({
    text,
    placeholder,
    wrap: !placeholder
  });
  switch (word) {
    case "title":
      return [free(draft.title.trim() || words.titlePlaceholder)];
    case "day":
      return [fixed(draft.repeat === "weekly" ? everyText(draft.day) : dayText(draft.day))];
    case "start":
      return [fixed(draft.allDay ? words.allDay : formatClock(draft.start, clock))];
    case "end":
      return [fixed(formatClock(draft.end ?? draft.start, clock))];
    case "who":
      return mode === "view" ? viewWho(props) : [free(whoWords(draft.tagIds, props.tags))];
    case "where":
      return [free(draft.location.trim() || words.wherePlaceholder)];
  }
}
function viewWho({
  draft,
  tags
}: EventSentenceProps): (Piece | string)[] {
  const people = tags.filter(tag => draft.tagIds.includes(tag.id));
  if (people.length === 0 || people.length === tags.length) {
    return [{
      text: whoWords(draft.tagIds, tags),
      placeholder: false,
      wrap: true
    }];
  }
  const many = people.length > VIEW_NAMED;
  const shown = many ? people.slice(0, VIEW_SHOWN) : people;
  const names = shown.flatMap((tag, index) => [...(index === 0 ? [] : [index === shown.length - 1 && !many ? words.and : words.comma]), {
    text: tag.name,
    placeholder: false,
    wrap: false,
    color: tag.color
  }]);
  if (!many) return names;
  return [...names, words.and, {
    text: words.others(people.length - VIEW_SHOWN),
    placeholder: false,
    wrap: false
  }];
}
function whoWords(tagIds: readonly string[], tags: readonly Tag[]): string {
  const who = whoText(tagIds, tags);
  if (who.kind === "everyone") return words.everyone;
  if (who.kind === "nobody") return words.nobody;
  return words.people(who.names, who.others);
}
function sameTimeText({
  tagIds,
  title,
  start,
  end,
  more
}: SameTime, tags: readonly Tag[], clock: Clock) {
  const who = whoText(tagIds, tags, 2);
  const names = who.kind === "people" ? words.people(who.names, who.others) : words.sameTimeEveryone;
  const one = who.kind !== "people" || who.names.length === 1 && who.others === 0;
  const range = `${formatClock(start, clock)} – ${formatClock(end, clock)}`;
  return words.sameTimeText(names, one, title, range, more);
}
function wordName(field: string, piece: Piece, mode: "edit" | "view"): string {
  if (mode === "view") return words.edit(field, piece.text);
  return words.change(field, piece.placeholder ? words.notSet : piece.text);
}
function dayText(day: string): string {
  const date = parseDateKey(day);
  return `${formatWeekday(date, "UTC")}, ${formatMonthDay(date, "UTC")}`;
}
function everyText(day: string): string {
  return words.every(formatLongWeekday(parseDateKey(day), "UTC"));
}